import { useEffect, useMemo, useState } from "react";
import { Plus, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useActiveCompany } from "../../context/ActiveCompanyContext";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import StatusBadge, { ActiveBadge } from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const CURRENT_YEAR = new Date().getFullYear();
const MIN_YEAR = 2022;
const MAX_YEAR = 2040;

const EMPTY_FORM = {
  name: "",
  organization_number: "",
  company_code: "",
  active_from: "2026-01-01",
  active_to: "",
  is_group_company: false,
  group_parent_id: "",
  customer_invoicing_model: "EXTERNAL",
  customer_invoicing_counterparty_id: "",
  ic_cost_account_rows: [],
};

const icRowsToDict = (rows) => {
  const out = {};
  for (const r of rows) {
    if (r.account_number.trim() && r.counterparty_id) out[r.account_number.trim()] = r.counterparty_id;
  }
  return out;
};

export default function CompaniesPage() {
  const { isAdmin } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const { year: activeYear, setYear: setPeriodYear } = useActiveCompany();
  const periodYear = activeYear ?? CURRENT_YEAR;
  const [periods, setPeriods] = useState([]);
  const [periodYears, setPeriodYears] = useState([]);

  const load = async () => {
    const res = await api.get("/companies");
    setCompanies(res.data);
    if (selected) {
      const updated = res.data.find((c) => c.id === selected.id);
      setSelected(updated || null);
    }
  };

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPeriods = async (companyId, year) => {
    try {
      const [p, y] = await Promise.all([
        api.get(`/companies/${companyId}/periods`, { params: { year } }),
        api.get(`/companies/${companyId}/periods/years`),
      ]);
      setPeriods(p.data);
      setPeriodYears(y.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    if (selected) loadPeriods(selected.id, periodYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, periodYear]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (company) => {
    setEditing(company);
    setForm({
      name: company.name,
      organization_number: company.organization_number || "",
      company_code: company.company_code,
      active_from: company.active_from,
      active_to: company.active_to || "",
      is_group_company: company.is_group_company,
      group_parent_id: company.group_parent_id || "",
      customer_invoicing_model: company.customer_invoicing_model || "EXTERNAL",
      customer_invoicing_counterparty_id: company.customer_invoicing_counterparty_id || "",
      ic_cost_account_rows: Object.entries(company.ic_cost_accounts || {}).map(([account_number, counterparty_id]) => ({ account_number, counterparty_id })),
    });
    setDialogOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      active_to: form.active_to || null,
      organization_number: form.organization_number || null,
      group_parent_id: form.group_parent_id || null,
      customer_invoicing_counterparty_id: form.customer_invoicing_model === "THROUGH_GROUP" ? (form.customer_invoicing_counterparty_id || null) : null,
      ic_cost_accounts: icRowsToDict(form.ic_cost_account_rows),
    };
    delete payload.ic_cost_account_rows;
    try {
      if (editing) {
        await api.patch(`/companies/${editing.id}`, payload);
        toast.success("Company updated");
      } else {
        await api.post("/companies", payload);
        toast.success("Company created");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleActive = async (company) => {
    try {
      await api.patch(`/companies/${company.id}`, { active: !company.active });
      toast.success(company.active ? "Company deactivated" : "Company activated");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const [createYearOpen, setCreateYearOpen] = useState(false);
  const [createYearValue, setCreateYearValue] = useState("");

  const uncreatedYears = useMemo(() => {
    const created = new Set(periodYears);
    const out = [];
    for (let y = MIN_YEAR; y <= MAX_YEAR; y++) if (!created.has(y)) out.push(y);
    return out;
  }, [periodYears]);

  const openCreateYear = () => {
    setCreateYearValue(String(uncreatedYears[0] ?? ""));
    setCreateYearOpen(true);
  };

  const createYear = async () => {
    const year = Number(createYearValue);
    try {
      const res = await api.post(`/companies/${selected.id}/periods/years`, { year });
      toast.success(res.data.created > 0 ? `Created ${res.data.created} periods for ${year}` : `Periods for ${year} already exist`);
      setCreateYearOpen(false);
      await loadPeriods(selected.id, periodYear);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const periodAction = async (period, action) => {
    try {
      await api.post(`/periods/${period.id}/${action}`);
      toast.success("Period updated");
      await loadPeriods(selected.id, periodYear);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const [reopenTarget, setReopenTarget] = useState(null);
  const [reopenComment, setReopenComment] = useState("");

  const confirmReopen = async () => {
    try {
      await api.post(`/periods/${reopenTarget.id}/reopen`, { comment: reopenComment });
      toast.success("Period reopened");
      setReopenTarget(null);
      setReopenComment("");
      await loadPeriods(selected.id, periodYear);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // Year-management selector stays bound to the shared ActiveYear and lists
  // created Financial Planning years only — uncreated years are offered solely
  // via the explicit Create year dialog.
  const yearOptions = useMemo(() => {
    const opts = [...periodYears].sort((a, b) => b - a);
    if (periodYear != null && !opts.includes(periodYear)) opts.push(periodYear);
    return opts;
  }, [periodYears, periodYear]);

  return (
    <div data-testid="companies-page">
      <PageHeader
        title="Companies"
        subtitle="Legal entities in the Elva Group structure"
        testId="companies-header"
        actions={
          isAdmin && (
            <Button onClick={openCreate} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="new-company-button">
              <Plus className="h-4 w-4 mr-1.5" /> New company
            </Button>
          )
        }
      />

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="companies-table">
        <table className="w-full">
          <thead>
            <tr className="bg-secondary/50 border-b border-slate-200">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Company</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Code</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Group</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Status</th>
              <th className="py-3 px-4" />
            </tr>
          </thead>
          <tbody>
            {companies.map((c) => (
              <tr
                key={c.id}
                className={`border-b border-slate-100 hover:bg-accent/40 transition-colors cursor-pointer ${selected?.id === c.id ? "bg-[#EBF3FE]/50" : ""}`}
                onClick={() => setSelected(c)}
                data-testid={`company-row-${c.company_code}`}
              >
                <td className="py-3 px-4">
                  <span className="font-semibold text-[#0052CC] text-sm">{c.name}</span>
                  {c.organization_number && (
                    <span className="text-xs text-slate-500 block">{c.organization_number}</span>
                  )}
                </td>
                <td className="py-3 px-4 text-sm text-slate-700">{c.company_code}</td>
                <td className="py-3 px-4">
                  {c.is_group_company && <StatusBadge tone="blue" testId={`company-group-badge-${c.id}`}>Group company</StatusBadge>}
                </td>
                <td className="py-3 px-4"><ActiveBadge active={c.active} testId={`company-status-${c.id}`} /></td>
                <td className="py-3 px-4 text-right">
                  {isAdmin && (
                    <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button variant="outline" size="sm" onClick={() => openEdit(c)} data-testid={`edit-company-${c.id}`}>Edit</Button>
                      <Button variant="outline" size="sm" onClick={() => toggleActive(c)} data-testid={`toggle-company-${c.id}`}>
                        {c.active ? "Deactivate" : "Activate"}
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {companies.length === 0 && (
          <EmptyState title="No companies" description="Create the first legal entity to get started." testId="companies-empty" />
        )}
      </div>

      {selected && (
        <div className="mt-6 bg-white border border-slate-200 rounded-lg p-5" data-testid="periods-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-[#0052CC]" /> Financial Periods — {selected.name}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">Open periods hold current forecast. Closed periods hold verified actuals.</p>
            </div>
            <div className="flex items-center gap-2">
              <Select value={String(periodYear)} onValueChange={(v) => setPeriodYear(Number(v))}>
                <SelectTrigger className="w-28" data-testid="period-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => (
                    <SelectItem key={y} value={String(y)} data-testid={`period-year-option-${y}`}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isAdmin && (
                <Button
                  onClick={openCreateYear}
                  disabled={uncreatedYears.length === 0}
                  title={uncreatedYears.length === 0 ? `All years ${MIN_YEAR}–${MAX_YEAR} are already created` : undefined}
                  className="bg-[#0052CC] hover:bg-[#0047B3]"
                  data-testid="create-year-button"
                >
                  Create year
                </Button>
              )}
            </div>
          </div>

          {periods.length === 0 ? (
            <EmptyState
              title={`No periods for ${periodYear}`}
              description={isAdmin ? 'Use "Create year" to generate Jan–Dec periods.' : "Periods have not been created for this year."}
              testId="periods-empty"
            />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="periods-grid">
              {periods.map((p) => (
                <div
                  key={p.id}
                  className={`border p-3 rounded-lg flex flex-col gap-2 transition-all ${
                    p.status === "CLOSED" ? "bg-secondary/50 border-slate-300" : "bg-white border-slate-200 hover:border-blue-300"
                  }`}
                  data-testid={`period-card-${p.month}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-800">{MONTHS[p.month - 1]}</span>
                    {p.status === "CLOSED" ? (
                      <StatusBadge tone="slate" testId={`period-status-${p.month}`}>Closed</StatusBadge>
                    ) : p.actual_status === "VERIFIED_ACTUAL" ? (
                      <StatusBadge tone="green" testId={`period-status-${p.month}`}>Verified</StatusBadge>
                    ) : (
                      <StatusBadge tone="amber" testId={`period-status-${p.month}`}>Open</StatusBadge>
                    )}
                  </div>
                  <span className="text-[11px] text-slate-500">
                    {p.actual_status === "VERIFIED_ACTUAL" ? "Verified actual" : "Forecast"}
                  </span>
                  {isAdmin && (
                    <div className="flex flex-wrap gap-1 mt-auto">
                      {p.status === "OPEN" && p.actual_status === "FORECAST" && (
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => periodAction(p, "verify")} data-testid={`period-verify-${p.month}`}>
                          Verify actual
                        </Button>
                      )}
                      {p.status === "OPEN" && p.actual_status === "VERIFIED_ACTUAL" && (
                        <>
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => periodAction(p, "unverify")} data-testid={`period-unverify-${p.month}`}>
                            Unverify
                          </Button>
                          <Button size="sm" className="h-7 text-xs bg-[#0052CC] hover:bg-[#0047B3]" onClick={() => periodAction(p, "close")} data-testid={`period-close-${p.month}`}>
                            Close
                          </Button>
                        </>
                      )}
                      {p.status === "CLOSED" && (
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setReopenTarget(p); setReopenComment(""); }} data-testid={`period-reopen-${p.month}`}>
                          Reopen
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={createYearOpen} onOpenChange={setCreateYearOpen}>
        <DialogContent data-testid="create-year-dialog">
          <DialogHeader>
            <DialogTitle>Create Financial Planning year</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Creates Jan–Dec open periods for {selected?.name}. Only years within {MIN_YEAR}–{MAX_YEAR} that are not yet created are offered.
            Your current working year is not changed.
          </p>
          <div className="space-y-1.5">
            <Label>Year</Label>
            <Select value={createYearValue} onValueChange={setCreateYearValue}>
              <SelectTrigger data-testid="create-year-select"><SelectValue placeholder="Select year" /></SelectTrigger>
              <SelectContent>
                {uncreatedYears.map((y) => (
                  <SelectItem key={y} value={String(y)} data-testid={`create-year-option-${y}`}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateYearOpen(false)} data-testid="create-year-cancel">Cancel</Button>
            <Button className="bg-[#0052CC] hover:bg-[#0047B3]" disabled={!createYearValue} onClick={createYear} data-testid="create-year-confirm">
              Create year
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reopenTarget} onOpenChange={(v) => !v && setReopenTarget(null)}>
        <DialogContent data-testid="period-reopen-dialog">
          <DialogHeader>
            <DialogTitle>Reopen {reopenTarget ? `${MONTHS[reopenTarget.month - 1]}` : ""}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Reopening a closed month requires a mandatory comment. It is stored with your user and timestamp for auditability.
          </p>
          <div className="space-y-1.5">
            <Label>Comment / reason</Label>
            <textarea
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm min-h-20"
              value={reopenComment}
              onChange={(e) => setReopenComment(e.target.value)}
              placeholder="Why is this month being reopened?"
              data-testid="period-reopen-comment-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenTarget(null)} data-testid="period-reopen-cancel">Cancel</Button>
            <Button className="bg-[#0052CC] hover:bg-[#0047B3]" disabled={!reopenComment.trim()} onClick={confirmReopen} data-testid="period-reopen-confirm">
              Reopen month
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="company-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit company" : "New company"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Company code</Label>
                <Input value={form.company_code} onChange={(e) => setForm({ ...form, company_code: e.target.value })} placeholder="ELVA-X" data-testid="company-code-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Organization number</Label>
                <Input value={form.organization_number} onChange={(e) => setForm({ ...form, organization_number: e.target.value })} data-testid="company-orgnr-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Active from</Label>
                <Input type="date" value={form.active_from} onChange={(e) => setForm({ ...form, active_from: e.target.value })} data-testid="company-active-from-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Active to (optional)</Label>
                <Input type="date" value={form.active_to} onChange={(e) => setForm({ ...form, active_to: e.target.value })} data-testid="company-active-to-input" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Switch checked={form.is_group_company} onCheckedChange={(v) => setForm({ ...form, is_group_company: v })} data-testid="company-group-switch" />
              Group company (only one allowed)
            </label>
            {!form.is_group_company && (
              <div className="space-y-1.5">
                <Label>Group parent (operating company of)</Label>
                <Select value={form.group_parent_id || "none"} onValueChange={(v) => setForm({ ...form, group_parent_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="company-parent-select"><SelectValue placeholder="Not linked to a group" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not linked to a group</SelectItem>
                    {companies.filter((c) => c.id !== editing?.id && !c.group_parent_id).map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`company-parent-${c.id}`}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Customer Invoicing Model</Label>
              <Select
                value={form.customer_invoicing_model}
                onValueChange={(v) => setForm({ ...form, customer_invoicing_model: v, customer_invoicing_counterparty_id: v === "THROUGH_GROUP" ? form.customer_invoicing_counterparty_id : "" })}
              >
                <SelectTrigger data-testid="company-invoicing-model-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="EXTERNAL" data-testid="invoicing-model-external">EXTERNAL — invoices customers directly</SelectItem>
                  <SelectItem value="THROUGH_GROUP" data-testid="invoicing-model-through-group">THROUGH_GROUP — invoices via the Group company</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Determines whether booked revenue is external or intercompany at consolidation. Never inferred.</p>
            </div>
            {form.customer_invoicing_model === "THROUGH_GROUP" && (
              <div className="space-y-1.5">
                <Label>Through Group Counterparty</Label>
                <Select value={form.customer_invoicing_counterparty_id || "none"} onValueChange={(v) => setForm({ ...form, customer_invoicing_counterparty_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="company-invoicing-counterparty-select"><SelectValue placeholder="Select the Group hub company" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {companies.filter((c) => c.id !== editing?.id).map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`invoicing-counterparty-${c.id}`}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Required for THROUGH_GROUP. Must be another legal company acting as the Group hub.</p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>IC Cost Accounts</Label>
              <p className="text-xs text-slate-500">Booked cost accounts that deterministically identify intercompany purchases from a counterparty. Only explicitly configured accounts are classified — no inference.</p>
              {form.ic_cost_account_rows.map((row, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`ic-cost-account-row-${i}`}>
                  <Input
                    value={row.account_number}
                    onChange={(e) => setForm({ ...form, ic_cost_account_rows: form.ic_cost_account_rows.map((r, j) => j === i ? { ...r, account_number: e.target.value } : r) })}
                    placeholder="Account no."
                    className="w-32"
                    data-testid={`ic-cost-account-number-${i}`}
                  />
                  <Select value={row.counterparty_id || "none"} onValueChange={(v) => setForm({ ...form, ic_cost_account_rows: form.ic_cost_account_rows.map((r, j) => j === i ? { ...r, counterparty_id: v === "none" ? "" : v } : r) })}>
                    <SelectTrigger className="flex-1" data-testid={`ic-cost-account-counterparty-${i}`}><SelectValue placeholder="Counterparty" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {companies.filter((c) => c.id !== editing?.id).map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, ic_cost_account_rows: form.ic_cost_account_rows.filter((_, j) => j !== i) })} data-testid={`ic-cost-account-remove-${i}`}>Remove</Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, ic_cost_account_rows: [...form.ic_cost_account_rows, { account_number: "", counterparty_id: "" }] })} data-testid="ic-cost-account-add">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add IC cost account
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="company-cancel-button">Cancel</Button>
            <Button onClick={save} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="company-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
